import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PositionSizeRule, TradingStrategyForPortfolio } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc.ts';
import { showToast } from '@/components/Layout/Toast';
import { Button, Callout, ExitCandidateSelect, OptionBuyingStrategySelect } from '@/components/UI';
import Modal from '@/components/UI/Modal';
import SearchSelect, { type SearchSelectItem } from '@/components/UI/SearchSelect';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { getUnixTime } from '@einhaus/meteoric-helpers';
import { baselineMatchesDeployment, baselineSizingMatches } from './portfolioBacktestBaselineSelection';
import InactiveStrategyCallout from './InactiveStrategyCallout';
import { LiveStrategyPositionSizing } from './LiveStrategyPositionSizing';
import { describeBestBacktestPreparation, formatBestBacktestSnapshotReadiness } from './bestBacktestSelectionReadiness';
import { LIVE_READINESS_RECHECK_INTERVAL_MS, describeLiveReadinessCallout } from '@/utils/liveReadinessCallout';

interface TradingProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    portfolioId: string | undefined;
    strategy: TradingStrategyForPortfolio | null;
    tradingProfileOptions: SearchSelectItem[];
    portfolioPositionSizeRules: PositionSizeRule[];
}

const TradingProfileModal: React.FC<TradingProfileModalProps> = ({
    isOpen,
    onClose,
    portfolioId,
    strategy,
    tradingProfileOptions,
    portfolioPositionSizeRules
}) => {
    const queryClient = useQueryClient();
    const [selectedTradingProfile, setSelectedTradingProfile] = useState<number | null>(null);
    const [optionBuyingStrategyIds, setOptionBuyingStrategyIds] = useState<number[]>([]);
    const [exitCandidateIds, setExitCandidateIds] = useState<number[]>([]);
    const [positionSizeRules, setPositionSizeRules] = useState<PositionSizeRule[] | undefined>(undefined);
    const [requestedConfigurationKey, setRequestedConfigurationKey] = useState<string | null>(null);
    const initializedStrategyIdRef = useRef<number | null>(null);
    const [winningChoiceChanged, setWinningChoiceChanged] = useState(false);
    const [winningBaselineId, setWinningBaselineId] = useState<number | null>(null);
    const [enableWinnerRefresh, setEnableWinnerRefresh] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            initializedStrategyIdRef.current = null;
            return;
        }
        if (!strategy || requestedConfigurationKey || initializedStrategyIdRef.current === strategy.id) return;
        setSelectedTradingProfile(strategy.tradingProfileId || null);
        setOptionBuyingStrategyIds(strategy.optionBuyingStrategyIds ?? []);
        setExitCandidateIds(strategy.exitCandidateIds ?? []);
        setPositionSizeRules(strategy.positionSizeRules ? structuredClone(strategy.positionSizeRules) : undefined);
        initializedStrategyIdRef.current = strategy.id;
        setWinningChoiceChanged(false);
        setWinningBaselineId(null);
        setEnableWinnerRefresh(false);
    }, [isOpen, requestedConfigurationKey, strategy]);

    const portfolioIdNumber = Number(portfolioId);
    const selection = useMemo(
        () => ({
            strategyId: strategy?.id ?? 0,
            tradingProfileId: selectedTradingProfile ?? 0,
            optionBuyingStrategyIds,
            exitCandidateIds
        }),
        [exitCandidateIds, optionBuyingStrategyIds, selectedTradingProfile, strategy?.id]
    );
    const winningQuery = useQuery({
        ...trpc.user.fetchPortfolioStrategyBacktestBaselines.queryOptions({ portfolioId: portfolioIdNumber }),
        enabled: isOpen && portfolioIdNumber > 0
    });
    const baselineChoicesQuery = useQuery({
        ...trpc.user.fetchPortfolioBacktests.queryOptions({}),
        enabled: isOpen && strategy !== null
    });
    const refreshSchedulesQuery = useQuery({
        ...trpc.user.fetchPortfolioBacktestRefreshSchedules.queryOptions({}),
        enabled: isOpen && strategy !== null
    });
    const existingWinner = winningQuery.data?.baselines.find((row) => row.strategyId === strategy?.id);
    const chosenBaselineId = winningChoiceChanged ? winningBaselineId : (existingWinner?.baselineBacktestId ?? null);
    const baselineChoices = (baselineChoicesQuery.data?.backtests ?? []).filter(
        (row) => row.status === 'complete' && !row.refreshAttemptId && row.strategiesSubscribed.some((item) => item.id === strategy?.id)
    );
    const chosenBaseline = baselineChoices.find((row) => row.id === chosenBaselineId);
    const winnerMatches = chosenBaseline ? baselineMatchesDeployment(chosenBaseline, selection) : true;
    const winnerSizingMatches = chosenBaseline
        ? baselineSizingMatches(chosenBaseline, selection.strategyId, positionSizeRules ?? portfolioPositionSizeRules)
        : null;
    const winnerSchedule = refreshSchedulesQuery.data?.schedules.find((row) => row.baselineBacktestId === chosenBaselineId);
    const invalidNewWinner =
        chosenBaselineId !== null && chosenBaselineId !== existingWinner?.baselineBacktestId && (!chosenBaseline || !winnerMatches);
    const configurationKey = useMemo(
        () => JSON.stringify({ selection, positionSizeRules: positionSizeRules ?? null, chosenBaselineId, enableWinnerRefresh }),
        [positionSizeRules, selection, chosenBaselineId, enableWinnerRefresh]
    );
    const isSaveRequested = requestedConfigurationKey === configurationKey;
    const canPreview = (isOpen || isSaveRequested) && portfolioIdNumber > 0 && selection.strategyId > 0 && selection.tradingProfileId > 0;

    const previewQuery = useQuery({
        ...trpc.user.previewPortfolioStrategyDeployment.queryOptions({
            portfolioId: portfolioIdNumber,
            selection
        }),
        enabled: canPreview,
        refetchInterval: (query) => {
            const coverage = query.state.data?.coverage;
            const selectionReadiness = query.state.data?.selection;
            const liveReadiness = query.state.data?.liveReadiness;
            if (liveReadiness && describeLiveReadinessCallout(liveReadiness).shouldRecheck) return LIVE_READINESS_RECHECK_INTERVAL_MS;
            return (coverage && !coverage.isReady && (coverage.pendingBacktests > 0 || coverage.missingBacktests > 0)) ||
                (selectionReadiness?.required && !selectionReadiness.isReady)
                ? 5000
                : false;
        }
    });

    const { mutate: ensureCoverage, isPending: isPreparingCoverage } = useMutation(
        trpc.user.ensurePortfolioStrategyDeploymentCoverage.mutationOptions({
            onSuccess: () => void previewQuery.refetch(),
            onError: (error) => {
                setRequestedConfigurationKey(null);
                showToast(error.message || 'Could not prepare this profile change.', 'error');
            }
        })
    );

    const { mutateAsync: saveDeployment, isPending: isSavingDeployment } = useTrpcMutation(
        trpc.user.savePortfolioSetting.mutationOptions({
            onSuccess: (result) => {
                if (!result.success) {
                    setRequestedConfigurationKey(null);
                    if (result.errorMessages?.some((message) => message.includes('changed in another session'))) {
                        void queryClient.invalidateQueries({
                            queryKey: trpc.user.fetchPortfolio.queryOptions({
                                id: portfolioIdNumber,
                                shouldFetchTrades: true
                            }).queryKey
                        });
                    }
                    return;
                }
                setRequestedConfigurationKey(null);
                void queryClient.invalidateQueries({ queryKey: trpc.user.fetchPortfolioStrategyBacktestBaselines.queryKey() });
                void queryClient.invalidateQueries({ queryKey: trpc.user.fetchPortfolioBacktestRefreshSchedules.queryKey() });
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({
                        id: portfolioIdNumber,
                        shouldFetchTrades: true
                    }).queryKey
                });
                onClose();
            },
            onError: () => setRequestedConfigurationKey(null)
        }),
        {
            successMessage: 'Live strategy deployment updated',
            fallbackErrorMessage: 'Failed to update the live strategy deployment'
        }
    );

    const catalog = previewQuery.data;
    const liveReadinessCallout = catalog?.liveReadiness ? describeLiveReadinessCallout(catalog.liveReadiness) : null;
    const coverage = catalog?.coverage;
    const selectionReadiness = catalog?.selection;
    const selectionSnapshotReadiness = selectionReadiness
        ? formatBestBacktestSnapshotReadiness({
              oldestAvailableAtTimestamp: selectionReadiness.oldestAvailableAtTimestamp,
              newestAvailableAtTimestamp: selectionReadiness.newestAvailableAtTimestamp,
              nowTimestamp: getUnixTime()
          })
        : null;
    const configurationOnly = catalog?.success === true && catalog.configurationOnly;
    const isReady =
        catalog?.success === true && (configurationOnly || (coverage?.isReady === true && selectionReadiness?.isReady === true));
    const hasEmptyPositionSizeOverride = positionSizeRules !== undefined && positionSizeRules.length === 0;
    const canRequestSave = catalog?.success === true && (configurationOnly || (coverage != null && selectionReadiness != null));

    const submitDeployment = useCallback(() => {
        if (!strategy || !canPreview || !isReady || isSavingDeployment || invalidNewWinner) return;

        void saveDeployment({
            portfolioId: portfolioIdNumber,
            settingType: 'strategySubscribedSettings',
            value: {
                ...selection,
                positionSizeRules: positionSizeRules ?? null,
                expectedSelectionRevision: strategy.selectionRevision ?? 0,
                ...(winningChoiceChanged || enableWinnerRefresh
                    ? { winningBacktest: { baselineBacktestId: chosenBaselineId, enableAutoRefresh: enableWinnerRefresh } }
                    : {})
            }
        });
    }, [
        canPreview,
        isReady,
        isSavingDeployment,
        portfolioIdNumber,
        positionSizeRules,
        saveDeployment,
        selection,
        strategy,
        invalidNewWinner,
        winningChoiceChanged,
        chosenBaselineId,
        enableWinnerRefresh
    ]);

    const startPreparation = useCallback(() => {
        setRequestedConfigurationKey(configurationKey);
        ensureCoverage({
            portfolioId: portfolioIdNumber,
            selection
        });
        showToast('Preparing this profile change. It will save automatically when ready.', 'success');
    }, [configurationKey, ensureCoverage, portfolioIdNumber, selection]);

    const handleSave = useCallback(() => {
        if (!canRequestSave || hasEmptyPositionSizeOverride || invalidNewWinner) return;
        if (isReady) {
            submitDeployment();
            return;
        }
        startPreparation();
    }, [canRequestSave, hasEmptyPositionSizeOverride, invalidNewWinner, isReady, startPreparation, submitDeployment]);

    useEffect(() => {
        if (!isSaveRequested || isPreparingCoverage || isSavingDeployment) return;
        if (isReady) {
            submitDeployment();
            return;
        }
        if (catalog?.success === false) {
            setRequestedConfigurationKey(null);
            return;
        }

        const retryTimer = window.setTimeout(() => {
            ensureCoverage({
                portfolioId: portfolioIdNumber,
                selection
            });
        }, 10_000);

        return () => window.clearTimeout(retryTimer);
    }, [
        catalog?.success,
        ensureCoverage,
        isPreparingCoverage,
        isReady,
        isSaveRequested,
        isSavingDeployment,
        portfolioIdNumber,
        selection,
        submitDeployment
    ]);

    useEffect(() => {
        if (requestedConfigurationKey && requestedConfigurationKey !== configurationKey) {
            setRequestedConfigurationKey(null);
        }
    }, [configurationKey, requestedConfigurationKey]);

    return (
        <Modal isOpen={isOpen} title={`Configure Live Deployment${strategy ? ` · ${strategy.name}` : ''}`} onClose={onClose}>
            <div className="space-y-5">
                {strategy && !strategy.active && (
                    <InactiveStrategyCallout strategyId={strategy.id}>
                        You can save this deployment now, but this subscription will not open positions until the strategy is activated
                        globally.
                    </InactiveStrategyCallout>
                )}

                <Callout title="These choices belong only to this portfolio and strategy." variant="info">
                    Other strategies and portfolios using the same trading profile are not changed. New live entries snapshot these choices.
                </Callout>

                <SearchSelect
                    id="tradingProfileSelect"
                    items={tradingProfileOptions}
                    label="Trading profile"
                    mode="single"
                    placeholder="Select a trading profile"
                    value={selectedTradingProfile}
                    onChange={setSelectedTradingProfile}
                />

                <div className="flex gap-4 text-sm">
                    <Link className="text-primary hover:text-blue-400" target="_blank" to="/user/portfolioTradingProfile/0">
                        Create profile
                    </Link>
                    <Link className="text-primary hover:text-blue-400" target="_blank" to="/user/tradingProfiles">
                        Copy shared profile
                    </Link>
                </div>

                <section className="space-y-2 rounded border border-zinc-700 p-3">
                    <label className="block text-sm font-medium" htmlFor="winning-portfolio-baseline">
                        Winning portfolio backtest
                    </label>
                    <select
                        id="winning-portfolio-baseline"
                        className="w-full rounded bg-zinc-800 p-2 text-sm"
                        value={chosenBaselineId ?? ''}
                        disabled={!winningQuery.data || !baselineChoicesQuery.data || isSavingDeployment}
                        onChange={(event) => {
                            setWinningChoiceChanged(true);
                            setWinningBaselineId(event.target.value ? Number(event.target.value) : null);
                            setEnableWinnerRefresh(false);
                        }}
                    >
                        <option value="">No baseline selected</option>
                        {baselineChoices.map((row) => (
                            <option key={row.id} value={row.id}>
                                #{row.id} · {row.name}
                                {baselineMatchesDeployment(row, selection) ? '' : ' · different selections'}
                            </option>
                        ))}
                    </select>
                    {(winningQuery.error || baselineChoicesQuery.error) && (
                        <p className="text-sm text-amber-300">
                            Could not load winning baselines. Your saved relationship will be preserved.
                        </p>
                    )}
                    <p className="text-xs text-zinc-400">
                        Record the completed original backtest whose strategy settings you chose. Selecting it does not change deployment
                        settings or account capital. You can set or clear this later.
                    </p>
                    {chosenBaseline && (
                        <Link className="text-sm text-blue-300" target="_blank" to={`/user/portfolioBacktestStats?id=${chosenBaseline.id}`}>
                            View baseline #{chosenBaseline.id}
                        </Link>
                    )}
                    {!winnerMatches && (
                        <p className="text-sm text-amber-300">
                            {invalidNewWinner
                                ? 'Match the trading profile, option policies and exits before linking this baseline.'
                                : 'This deployment now differs from its saved winning baseline. The link remains as provenance; choose another winner if appropriate.'}
                        </p>
                    )}
                    {chosenBaseline && winnerSizingMatches === false && (
                        <p className="text-xs text-amber-300">
                            Position sizing differs from this research baseline. The account allocation and research capital remain
                            independent.
                        </p>
                    )}
                    {chosenBaseline &&
                        (winnerSchedule?.isEnabled ? (
                            <p className="text-sm text-blue-300">
                                Automatic refresh is already on.{' '}
                                <Link to={`/user/portfolioBacktestStats?id=${chosenBaseline.id}`}>Manage refresh settings</Link>
                            </p>
                        ) : (
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={enableWinnerRefresh}
                                    disabled={!refreshSchedulesQuery.data || !chosenBaseline.trainingEndDateTime || isSavingDeployment}
                                    onChange={(event) => setEnableWinnerRefresh(event.target.checked)}
                                />
                                Keep this winning backtest updated every 14 days
                            </label>
                        ))}
                    {chosenBaseline && (
                        <p className="text-xs text-zinc-400">
                            {winnerSchedule
                                ? `Existing refresh selections (${winnerSchedule.configurationMode === 'followPortfolio' ? 'follow the original portfolio' : 'keep backtest selections'}) are preserved.`
                                : chosenBaseline.portfolioId === portfolioIdNumber
                                  ? 'Refreshes follow this portfolio’s current strategy selections.'
                                  : 'This baseline belongs to another portfolio. New refreshes keep its selected profiles and exits, using their current definitions; they do not follow this deployment.'}{' '}
                            Clearing the winner link does not pause its refresh schedule.
                        </p>
                    )}
                </section>

                <LiveStrategyPositionSizing
                    portfolioRules={portfolioPositionSizeRules}
                    rules={positionSizeRules}
                    onChange={setPositionSizeRules}
                />

                {canPreview && previewQuery.isLoading && <p className="text-sm text-zinc-400">Loading available deployment choices…</p>}

                {catalog && (
                    <>
                        {catalog.isOptionsStrategy && (
                            <OptionBuyingStrategySelect
                                allowedIds={catalog.optionBuyingStrategies.map((item) => item.id)}
                                id="portfolioStrategyOptionBuyingStrategies"
                                label="Option buying strategies (OBS)"
                                mode="multi"
                                noOptionsMessage="No active option buying strategies are available"
                                value={optionBuyingStrategyIds}
                                onChange={setOptionBuyingStrategyIds}
                            />
                        )}

                        <ExitCandidateSelect
                            allowedIds={catalog.exitCandidates.map((candidate) => candidate.id)}
                            id="portfolioStrategyExitCandidates"
                            label="Exit candidates"
                            mode="multi"
                            noOptionsMessage="No active exit candidates are attached to this strategy"
                            value={exitCandidateIds}
                            onChange={setExitCandidateIds}
                        />
                    </>
                )}

                {configurationOnly && (
                    <Callout title="Inactive research configuration" variant="info">
                        Save this setup for historical portfolio comparisons. Trading remains inactive; this does not prepare live coverage
                        or arm a paper order.
                    </Callout>
                )}
                {liveReadinessCallout ? (
                    <Callout title={liveReadinessCallout.title} variant={liveReadinessCallout.variant}>
                        <p>{liveReadinessCallout.reason}</p>
                        <p className="mt-1 text-xs">{liveReadinessCallout.guidance}</p>
                    </Callout>
                ) : (
                    catalog?.errorMessages.map((message) => (
                        <Callout key={message} title="Deployment is not ready" variant="error">
                            {message}
                        </Callout>
                    ))
                )}

                {coverage && (
                    <Callout
                        title={
                            coverage.isReady
                                ? 'Historical data ready'
                                : isSaveRequested
                                  ? 'Preparing historical data'
                                  : 'Historical data needs preparation'
                        }
                        variant={coverage.isReady ? 'success' : coverage.failedBacktests > 0 ? 'error' : 'warning'}
                    >
                        <p>
                            {coverage.readyBacktests} of {coverage.expectedBacktests} combinations ready
                            {coverage.pendingBacktests > 0 ? ` · ${coverage.pendingBacktests} pending` : ''}
                            {coverage.missingBacktests > 0 ? ` · ${coverage.missingBacktests} missing` : ''}
                            {coverage.failedBacktests > 0 ? ` · ${coverage.failedBacktests} failed` : ''}
                        </p>
                        <p className="mt-1 text-xs">
                            The system reuses compatible results and prepares anything missing automatically when you save.
                        </p>
                    </Callout>
                )}

                {selectionReadiness && selectionReadiness.required && (
                    <Callout
                        title={
                            selectionReadiness.isReady
                                ? 'Selection data ready'
                                : isSaveRequested
                                  ? 'Preparing selection data'
                                  : 'Selection data needs preparation'
                        }
                        variant={selectionReadiness.isReady ? 'success' : selectionReadiness.status === 'failed' ? 'error' : 'warning'}
                    >
                        <p>
                            {selectionReadiness.isReady
                                ? 'This profile has everything it needs.'
                                : describeBestBacktestPreparation(selectionReadiness.reason)}
                        </p>
                        {selectionSnapshotReadiness && <p className="mt-1 text-xs">{selectionSnapshotReadiness}</p>}
                        <details className="mt-2 text-xs text-zinc-400">
                            <summary className="cursor-pointer">Technical details</summary>
                            <p className="mt-1">
                                {selectionReadiness.readyTargetCount} of {selectionReadiness.targetCount} targets ready · scope{' '}
                                {selectionReadiness.selectionScope} · policy {selectionReadiness.policyKey} ·{' '}
                                {selectionReadiness.executionMode}
                                {selectionReadiness.reason ? ` · ${selectionReadiness.reason}` : ''}
                            </p>
                        </details>
                    </Callout>
                )}

                {isSaveRequested && !isReady && (
                    <Callout title="Preparing and saving this profile change" variant="info">
                        <p>Your current live settings stay in place until the new configuration is ready.</p>
                        <p className="mt-1 text-xs">Preparation continues automatically; no additional button presses are needed.</p>
                    </Callout>
                )}

                <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="secondary" onClick={onClose}>
                        {isSaveRequested ? 'Hide' : 'Cancel'}
                    </Button>
                    <Button
                        disabled={
                            invalidNewWinner ||
                            !canRequestSave ||
                            hasEmptyPositionSizeOverride ||
                            isSaveRequested ||
                            isPreparingCoverage ||
                            isSavingDeployment
                        }
                        title={
                            hasEmptyPositionSizeOverride
                                ? 'Add at least one strategy position-sizing rule or use portfolio sizing.'
                                : undefined
                        }
                        variant="primary"
                        onClick={handleSave}
                    >
                        {isSavingDeployment
                            ? 'Saving…'
                            : isSaveRequested || isPreparingCoverage
                              ? 'Preparing and saving…'
                              : configurationOnly
                                ? 'Save inactive configuration'
                                : isReady
                                  ? 'Save deployment'
                                  : 'Save and prepare'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default TradingProfileModal;
